import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import LoginPage from './components/LoginPage';
import { apiScopes } from './authConfig';
import './styles/crm-animations.css';
import { COLORS, Icons, Avatar } from './components/ui/crm-ui';
import { UserProvider, useRole } from './context/UserContext';
import DashboardView from './components/views/DashboardView';
import ClientsView from './components/views/ClientsView';
import DocumentsView from './components/views/DocumentsView';
import PromptsView from './components/views/PromptsView';
import PromptsGenerateView from './components/PromptsView';
import ApiTestPanel from './components/ApiTestPanel';
import NotificationBell from './components/NotificationBell';
import DispatchesView from './components/views/DispatchesView';

const RAW_API_URL = (import.meta.env.VITE_API_URL || '').trim();

function normalizeApiUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[a-z0-9.-]+(?::\d+)?$/i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
}

const BASE_API_URL = normalizeApiUrl(RAW_API_URL);

function getApiConfigError(): string | null {
  if (!BASE_API_URL) return 'REACT_APP_API_URL is missing in the frontend build configuration.';
  try {
    const apiUrl = new URL(BASE_API_URL);
    if (apiUrl.origin === window.location.origin) {
      return 'REACT_APP_API_URL points to the frontend origin. It must point to the backend App Service URL.';
    }
    if (apiUrl.protocol !== 'https:' && apiUrl.hostname !== 'localhost' && apiUrl.hostname !== '127.0.0.1') {
      return 'REACT_APP_API_URL must use HTTPS for non-local environments.';
    }
  } catch {
    return 'REACT_APP_API_URL is not a valid absolute URL.';
  }
  return null;
}

const API_CONFIG_ERROR = getApiConfigError();

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: (color?: string) => JSX.Element;
}

const NAV: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/',          icon: Icons.dashboard },
  { id: 'clients',   label: 'Clients',   path: '/clients',   icon: Icons.clients },
  { id: 'documents', label: 'Documents', path: '/documents', icon: Icons.docs },
  { id: 'prompts',   label: 'Prompts',   path: '/prompts',   icon: Icons.prompts },
  { id: 'generate',  label: 'Generate',  path: '/prompts/generate', icon: Icons.agent },
  { id: 'dispatches', label: 'Dispatches', path: '/dispatches', icon: Icons.agent },
  // Dev-only — remove before release
  { id: 'apitest',   label: 'API Test',  path: '/test',      icon: Icons.agent },
];

function initialsFromName(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const AUDIT_NAV = ['generate', 'clients', 'documents', 'dispatches'];

function MainApp() {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const { role, loading } = useRole();
  const visibleNav = role === 'audit'
    ? NAV.filter((n) => AUDIT_NAV.includes(n.id)).sort((a, b) => AUDIT_NAV.indexOf(a.id) - AUDIT_NAV.indexOf(b.id))
    : NAV;
  const [profileMenu, setProfileMenu] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);

  // Axios interceptor — attach the bearer token to every request to BASE_API_URL.
  // (Preserved verbatim from Mitch's previous App.tsx.)
  useEffect(() => {
    const id = axios.interceptors.request.use(async (config) => {
      if (!API_CONFIG_ERROR && accounts.length > 0 && apiScopes.length > 0 && BASE_API_URL && config.url?.startsWith(BASE_API_URL)) {
        try {
          const { accessToken } = await instance.acquireTokenSilent({
            scopes: apiScopes,
            account: accounts[0],
          });
          config.headers = config.headers ?? {};
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            await instance.acquireTokenRedirect({
              scopes: apiScopes,
              account: accounts[0],
            });
          }
          throw err;
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [instance, accounts]);

  // Close profile menu on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileMenu(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const user = accounts[0];
  const userName = user?.name || user?.username || '';
  const firstName = userName.split(' ')[0] || '';
  const initials = useMemo(() => initialsFromName(userName), [userName]);

  if (loading) return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: COLORS.pageBg }}>
      <div style={{ fontSize: 13, color: COLORS.mid, fontWeight: 500 }}>Loading...</div>
    </div>
  );

  function handleSignOut() {
    instance.logoutRedirect();
  }

  return (
    <div className="crm-app" style={{ display: 'flex', height: '100vh', background: COLORS.pageBg, color: COLORS.dark }}>
      {/* Sidebar */}
      <aside style={{
        width: 220, background: COLORS.white,
        borderRight: `1px solid ${COLORS.border}`,
        display: 'flex', flexDirection: 'column', flexShrink: 0,
      }}>
        <div style={{ padding: '18px 18px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/sd-logo.png" alt="Saward Dawson" style={{ height: 36, width: 36, borderRadius: 8 }} />
          <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>Saward Dawson</div>
        </div>

        <nav style={{ flex: 1, padding: '8px 10px' }}>
          {visibleNav.map((n) => {
            const active = n.path === '/'
              ? location.pathname === '/'
              : n.path === '/prompts'
                ? location.pathname === '/prompts'
                : location.pathname.startsWith(n.path);
            return (
              <Link
                key={n.id}
                to={n.path}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '9px 12px', borderRadius: 8,
                  background: active ? COLORS.blueSoft : 'transparent',
                  color: active ? COLORS.blue : COLORS.mid,
                  fontSize: 13, fontWeight: active ? 600 : 500,
                  textDecoration: 'none', marginBottom: 2,
                  transition: 'all 0.12s',
                }}
              >
                {n.icon(active ? COLORS.blue : COLORS.mid)} {n.label}
              </Link>
            );
          })}
        </nav>

        {user && (
          <div style={{ padding: '14px 16px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar initials={initials} size={32} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
              <div style={{ fontSize: 11, color: COLORS.mid }}>Signed in</div>
            </div>
          </div>
        )}
      </aside>

      {/* Main pane */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar */}
        <div style={{
          height: 54, background: COLORS.white,
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', padding: '0 24px', gap: 14, flexShrink: 0,
        }}>
          <div style={{ flex: 1 }} />

          <NotificationBell baseApiUrl={BASE_API_URL} />

          {user && (
            <div ref={profileRef} style={{ marginLeft: 'auto', position: 'relative' }}>
              <div
                onClick={() => setProfileMenu((m) => !m)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  padding: '4px 8px', borderRadius: 8, transition: 'background 0.15s',
                  background: profileMenu ? COLORS.light : 'transparent',
                }}
              >
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{userName}</div>
                  <div style={{ fontSize: 11, color: COLORS.mid }}>{user.username}</div>
                </div>
                <Avatar initials={initials} size={32} />
                {Icons.chevDown(COLORS.mid)}
              </div>
              {profileMenu && (
                <div
                  className="crm-anim-slideDown"
                  style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 6,
                    background: COLORS.white, border: `1px solid ${COLORS.border}`,
                    borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    zIndex: 200, width: 200, overflow: 'hidden',
                  }}
                >
                  <div
                    onClick={() => { handleSignOut(); setProfileMenu(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', fontSize: 13, fontWeight: 500,
                      cursor: 'pointer', color: COLORS.red, transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.redSoft)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {Icons.logout(COLORS.red)} Sign out
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Page content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {API_CONFIG_ERROR && (
            <div style={{
              marginBottom: 16, borderRadius: 8,
              border: `1px solid ${COLORS.red}33`, background: COLORS.redSoft,
              padding: '12px 16px', fontSize: 13, color: COLORS.red,
            }}>
              <p style={{ fontWeight: 600, margin: 0 }}>API configuration error</p>
              <p style={{ margin: '4px 0 0' }}>{API_CONFIG_ERROR}</p>
              <p style={{ margin: '4px 0 0', wordBreak: 'break-all' }}>Current value: {RAW_API_URL || '(empty)'}</p>
            </div>
          )}

          <Routes>
            <Route path="/"          element={
              role === 'audit'
                ? <Navigate to="/prompts/generate" replace />
                : <DashboardView baseApiUrl={BASE_API_URL} userFirstName={firstName} />
            } />
            <Route path="/clients"   element={<ClientsView   baseApiUrl={BASE_API_URL} />} />
            <Route path="/documents" element={<DocumentsView baseApiUrl={BASE_API_URL} />} />
            <Route path="/prompts"          element={<PromptsView         baseApiUrl={BASE_API_URL} />} />
            <Route path="/prompts/generate" element={<PromptsGenerateView baseApiUrl={BASE_API_URL} />} />
            <Route path="/dispatches"       element={<DispatchesView      baseApiUrl={BASE_API_URL} />} />
            <Route path="/test"             element={<ApiTestPanel        baseApiUrl={BASE_API_URL} />} />
            <Route path="*"          element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <UserProvider>
        <AuthenticatedTemplate>
          <MainApp />
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <LoginPage />
        </UnauthenticatedTemplate>
      </UserProvider>
    </BrowserRouter>
  );
}
